<div class="admin-users">
  <h1 id="users-table-heading" class="mb-4 text-2xl font-semibold text-gray-800">Users</h1>

  <div class="table-card">
    <div class="table-toolbar">
      <div class="table-toolbar__filters">
        <mat-form-field appearance="outline">
          <mat-label>Search text</mat-label>
          <mat-icon matPrefix class="mr-2 text-gray-400">search</mat-icon>
          <input matInput [formControl]="searchControl" placeholder="Email, name, nickname..." />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>State code</mat-label>
          <input matInput [formControl]="stateCodeControl" placeholder="e.g. LA" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>State</mat-label>
          <mat-select [formControl]="stateControl">
            <mat-option value="">All states</mat-option>
            <mat-option *ngFor="let state of stateOptions" [value]="state.id">
              {{ state.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>LGA</mat-label>
          <mat-select [formControl]="lgaControl" [disabled]="!stateControl.value">
            <mat-option value="">All LGAs</mat-option>
            <mat-option *ngFor="let lga of lgaOptions" [value]="lga.id">
              {{ lga.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="table-toolbar__actions">
        <button mat-raised-button color="warn" (click)="openBlockForm()">Block user</button>
      </div>
    </div>

    <table mat-table [dataSource]="users" aria-labelledby="users-table-heading">
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef>Name</th>
        <td mat-cell *matCellDef="let u">
          <div class="cell-identity">
            <span class="cell-identity__avatar">{{ u | initials }}</span>
            <div class="cell-identity__text">
              <div class="cell-identity__primary">{{ u.first_name }} {{ u.last_name || '' }}</div>
              <div class="cell-identity__secondary">{{ u.email || '—' }}</div>
            </div>
          </div>
        </td>
      </ng-container>
      <ng-container matColumnDef="state_code">
        <th mat-header-cell *matHeaderCellDef>State code</th>
        <td mat-cell *matCellDef="let u">{{ u.state_code || '—' }}</td>
      </ng-container>
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Actions</th>
        <td mat-cell *matCellDef="let u">
          <div class="table-actions">
            <button
              type="button"
              class="table-icon-btn table-icon-btn--danger"
              (click)="blockFromTable(u)"
              [attr.aria-label]="'Block ' + userDisplay(u)"
              matTooltip="Block user"
            >
              <mat-icon>block</mat-icon>
            </button>
            <button
              type="button"
              class="table-icon-btn table-icon-btn--danger"
              (click)="deleteUser(u)"
              [attr.aria-label]="'Delete ' + userDisplay(u)"
              matTooltip="Delete user"
            >
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="['name', 'state_code', 'actions']"></tr>
      <tr mat-row *matRowDef="let row; columns: ['name', 'state_code', 'actions']"></tr>
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell py-4 text-center text-gray-500" colspan="3">
          <span *ngIf="loading">Loading…</span>
          <span *ngIf="!loading && users.length === 0">No users found</span>
        </td>
      </tr>
    </table>
    <mat-paginator
      [length]="totalUsers ?? 0"
      [pageIndex]="page - 1"
      [pageSize]="limit"
      [pageSizeOptions]="pageSizeOptions"
      [showFirstLastButtons]="true"
      (page)="onUsersPageChange($event)"
    ></mat-paginator>
  </div>

  <!-- Block user dialog (inline) -->
  <div
    *ngIf="showBlockForm"
    class="fixed inset-0 z-20 flex items-center justify-center bg-black/50"
    (click)="closeBlockForm()"
  >
    <div
      class="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      (click)="$event.stopPropagation()"
    >
      <h2 class="mb-4 text-lg font-semibold">Block user</h2>
      <form [formGroup]="blockForm" (ngSubmit)="submitBlock()">
        <mat-form-field class="w-full" appearance="outline">
          <mat-label>Email</mat-label>
          <input matInput formControlName="email" placeholder="user@example.com" />
        </mat-form-field>
        <mat-form-field class="w-full" appearance="outline">
          <mat-label>State code</mat-label>
          <input matInput formControlName="state_code" placeholder="e.g. LA" />
        </mat-form-field>
        <mat-form-field class="w-full" appearance="outline">
          <mat-label>Reason (optional)</mat-label>
          <input matInput formControlName="reason" placeholder="Reason for block" />
        </mat-form-field>
        <div class="flex justify-end gap-2">
          <button mat-button type="button" (click)="closeBlockForm()">Cancel</button>
          <button mat-raised-button color="warn" type="submit" [disabled]="blocking">Block</button>
        </div>
      </form>
    </div>
  </div>

  <!-- Blocked list -->
  <div class="table-card mt-8">
    <div class="table-toolbar">
      <h2 id="blocked-users-heading" class="table-toolbar__heading">Blocked users</h2>
    </div>
    <table mat-table [dataSource]="blocked" aria-labelledby="blocked-users-heading">
      <ng-container matColumnDef="identifier">
        <th mat-header-cell *matHeaderCellDef>Blocked user</th>
        <td mat-cell *matCellDef="let b">
          <div class="cell-identity">
            <span class="cell-identity__avatar">{{ b | initials }}</span>
            <div class="cell-identity__text">
              <div class="cell-identity__primary">{{ b.email ?? b.state_code ?? b.identifier ?? '—' }}</div>
            </div>
          </div>
        </td>
      </ng-container>
      <ng-container matColumnDef="reason">
        <th mat-header-cell *matHeaderCellDef>Reason</th>
        <td mat-cell *matCellDef="let b">{{ b.reason || '—' }}</td>
      </ng-container>
      <ng-container matColumnDef="unblock">
        <th mat-header-cell *matHeaderCellDef>Actions</th>
        <td mat-cell *matCellDef="let b">
          <div class="table-actions">
            <button
              type="button"
              class="table-icon-btn"
              (click)="unblock(b)"
              [attr.aria-label]="'Unblock ' + (b.email ?? b.state_code ?? b.identifier)"
              matTooltip="Unblock"
            >
              <mat-icon>lock_open</mat-icon>
            </button>
          </div>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="['identifier', 'reason', 'unblock']"></tr>
      <tr mat-row *matRowDef="let row; columns: ['identifier', 'reason', 'unblock']"></tr>
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell py-4 text-center text-gray-500" colspan="3">
          <span *ngIf="loadingBlocked">Loading…</span>
          <span *ngIf="!loadingBlocked && blocked.length === 0">No blocked users</span>
        </td>
      </tr>
    </table>
    <mat-paginator
      [length]="totalBlocked ?? 0"
      [pageIndex]="blockedPage - 1"
      [pageSize]="blockedLimit"
      [pageSizeOptions]="pageSizeOptions"
      [showFirstLastButtons]="true"
      (page)="onBlockedPageChange($event)"
    ></mat-paginator>
  </div>

  <!-- Deleted list -->
  <div class="table-card mt-8">
    <div class="table-toolbar">
      <h2 id="deleted-users-heading" class="table-toolbar__heading">Deleted users</h2>
    </div>
    <table mat-table [dataSource]="deleted" aria-labelledby="deleted-users-heading">
      <ng-container matColumnDef="identifier">
        <th mat-header-cell *matHeaderCellDef>Deleted user</th>
        <td mat-cell *matCellDef="let d">
          <div class="cell-identity">
            <span class="cell-identity__avatar">{{ d | initials }}</span>
            <div class="cell-identity__text">
              <div class="cell-identity__primary">{{ d.first_name ?? '' }} {{ d.last_name ?? '' }}</div>
              <div class="cell-identity__secondary">{{ d.email ?? d.state_code ?? d.identifier ?? '—' }}</div>
            </div>
          </div>
        </td>
      </ng-container>
      <ng-container matColumnDef="deletedAt">
        <th mat-header-cell *matHeaderCellDef>Deleted at</th>
        <td mat-cell *matCellDef="let d">{{ d.deleted_at ?? d.updated_at ?? d.created_at ?? '—' }}</td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="['identifier', 'deletedAt']"></tr>
      <tr mat-row *matRowDef="let row; columns: ['identifier', 'deletedAt']"></tr>
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell py-4 text-center text-gray-500" colspan="2">
          <span *ngIf="loadingDeleted">Loading…</span>
          <span *ngIf="!loadingDeleted && deleted.length === 0">No deleted users</span>
        </td>
      </tr>
    </table>
    <mat-paginator
      [length]="totalDeleted ?? 0"
      [pageIndex]="deletedPage - 1"
      [pageSize]="deletedLimit"
      [pageSizeOptions]="pageSizeOptions"
      [showFirstLastButtons]="true"
      (page)="onDeletedPageChange($event)"
    ></mat-paginator>
  </div>
</div>
